/**
 * PR lines from Excel: the template's columns, the column names PRPO recognises (shared by the
 * Import dialog and Ctrl+V on the grid), and the parsing of a sheet or pasted cells into lines.
 * Pure: the item check against SAP and the grid update live in CreatePR / PRLineImportDialog.
 *
 * The columns follow the PR grid: Division, Department, WBS (cost code), Auxiliary and LOC are
 * the grid's dim1..dim5.
 */

export type ImportField =
  | 'itemcode' | 'itemdescription' | 'u_item_details' | 'quantity' | 'uomcode' | 'unitprice'
  | 'deliverydate' | 'projectcode' | 'dim1' | 'dim2' | 'dim3' | 'dim4' | 'dim5'
  | 'vendorcode' | 'warehouse' | 'u_ser_qty' | 'u_ser_unit_price';

export interface ImportColumn {
  field: ImportField;
  header: string;
  aliases: string[];
  kind: 'text' | 'number' | 'date';
  note: string;
  serviceOnly?: boolean;
}

export const PR_IMPORT_COLUMNS: ImportColumn[] = [
  { field: 'itemcode', header: 'Item Code', aliases: ['item code', 'itemcode', 'item no.', 'item no', 'item', 'item #'], kind: 'text', note: 'SAP item code. Item Code or Item Description is required.' },
  { field: 'itemdescription', header: 'Item Description', aliases: ['item description', 'description', 'item name', 'name'], kind: 'text', note: 'Filled from SAP when the item code is known.' },
  { field: 'u_item_details', header: 'Line Remarks', aliases: ['line remarks', 'item details', 'u_item_details', 'remarks'], kind: 'text', note: 'Optional.' },
  { field: 'quantity', header: 'Req. Qty', aliases: ['req. qty', 'req qty', 'qty', 'quantity', 'required qty', 'required quantity'], kind: 'number', note: 'Required, more than 0.' },
  { field: 'uomcode', header: 'UoM', aliases: ['uom', 'unit', 'uom code', 'unit of measure'], kind: 'text', note: 'Filled from SAP when the item code is known.' },
  { field: 'unitprice', header: 'Unit Price', aliases: ['unit price', 'price'], kind: 'number', note: 'Optional; taken from SAP when blank.' },
  { field: 'deliverydate', header: 'Delivery Date', aliases: ['delivery date', 'required date', 'date'], kind: 'date', note: 'Any date format: an Excel date, 30/11/2026, 30-11-26, 11/30/2026, 30-Nov-2026, 2026-11-30…; the PR required date when blank.' },
  { field: 'projectcode', header: 'Project', aliases: ['project', 'project code'], kind: 'text', note: 'SAP project code; its division, department, auxiliary, LOC and warehouse are filled in when left blank.' },
  { field: 'dim1', header: 'Division', aliases: ['division', 'division code', 'dim1'], kind: 'text', note: 'Optional dimension code.' },
  { field: 'dim2', header: 'Department', aliases: ['department', 'department code', 'dim2'], kind: 'text', note: 'Optional dimension code.' },
  { field: 'dim3', header: 'WBS', aliases: ['wbs', 'cost code', 'cost code (wbs)', 'project cost code', 'section', 'dim3'], kind: 'text', note: 'Optional dimension code; filled from the item group when blank.' },
  { field: 'dim4', header: 'Auxiliary', aliases: ['auxiliary', 'auxiliary code', 'aux', 'dim4'], kind: 'text', note: 'Optional dimension code.' },
  { field: 'dim5', header: 'LOC', aliases: ['loc', 'loc code', 'location', 'dim5'], kind: 'text', note: 'Optional dimension code.' },
  { field: 'vendorcode', header: 'Vendor', aliases: ['vendor', 'vendor code', 'supplier'], kind: 'text', note: 'Optional SAP vendor code.' },
  { field: 'warehouse', header: 'Warehouse', aliases: ['warehouse', 'warehouse code', 'whs'], kind: 'text', note: 'Optional SAP warehouse code.' },
  { field: 'u_ser_qty', header: 'Service Qty', aliases: ['service qty', 'service quantity', 'u_ser_qty'], kind: 'number', note: 'Service PRs: the real quantity of the service.', serviceOnly: true },
  { field: 'u_ser_unit_price', header: 'Service Unit Price', aliases: ['service unit price', 'u_ser_unit_price'], kind: 'number', note: 'Service PRs: the real unit price of the service.', serviceOnly: true },
];

const BY_ALIAS = new Map<string, ImportColumn>();
for (const column of PR_IMPORT_COLUMNS) for (const alias of column.aliases) BY_ALIAS.set(alias, column);

const normalizeHeader = (value: unknown): string => String(value ?? '').replace(/\s+/g, ' ').trim().toLowerCase();

/** The PR line field a column name stands for (exact match on a known name, any case), or null. */
export function fieldForHeader(header: unknown): ImportField | null {
  return BY_ALIAS.get(normalizeHeader(header))?.field ?? null;
}

/** The template's columns for a PR type (the service columns only on Service PRs). */
export function templateColumns(requisitionType: string): ImportColumn[] {
  return PR_IMPORT_COLUMNS.filter((c) => !c.serviceOnly || requisitionType === 'Service');
}

/** A row of column names: at least one cell is a known name and no cell is a number. */
export function isHeaderRow(cells: unknown[]): boolean {
  const filled = cells.map((c) => String(c ?? '').trim()).filter(Boolean);
  if (!filled.length || filled.some((c) => /^-?[\d.,]+$/.test(c))) return false;
  return filled.some((c) => fieldForHeader(c) !== null);
}

/** Cells pasted from Excel: tab-separated rows; a quoted cell may hold tabs, newlines and "". */
export function textToTable(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  const src = String(text ?? '').replace(/\r\n?/g, '\n');
  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (quoted) {
      if (ch === '"' && src[i + 1] === '"') { cell += '"'; i++; }
      else if (ch === '"') quoted = false;
      else cell += ch;
    } else if (ch === '"' && cell === '') quoted = true;
    else if (ch === '\t') { row.push(cell); cell = ''; }
    else if (ch === '\n') { row.push(cell); rows.push(row); row = []; cell = ''; }
    else cell += ch;
  }
  if (cell !== '' || row.length) { row.push(cell); rows.push(row); }
  return rows.filter((r) => r.some((c) => c.trim() !== ''));
}

/** A number from a cell: 1,234.50 / SAR 12 / 12 % are read; '' is null; anything else is NaN. */
export function parseNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : NaN;
  const s = String(value ?? '').trim();
  if (!s) return null;
  const cleaned = s.replace(/[,\s]/g, '').replace(/^[A-Za-z$€£]+/, '').replace(/[A-Za-z%]+$/, '');
  if (!/^-?\d*\.?\d+$/.test(cleaned)) return NaN;
  return Number(cleaned);
}

const pad = (n: number) => String(n).padStart(2, '0');
const isoDate = (y: number, m: number, d: number): string | null => {
  const date = new Date(Date.UTC(y, m - 1, d));
  if (date.getUTCFullYear() !== y || date.getUTCMonth() !== m - 1 || date.getUTCDate() !== d) return null;
  return `${y}-${pad(m)}-${pad(d)}`;
};

/*
 * Dates. Excel copies a date as the PC shows it, and every PC has its own format: 30/11/2026,
 * 11/30/2026, 30-11-26, 30.11.2026, 2026/11/30, 30-Nov-26, 30 نوفمبر 2026, Arabic digits, a
 * weekday or a time, even a Hijri date on Saudi PCs. All of them become YYYY-MM-DD. The only
 * real ambiguity is day/month order when both are 12 or less (05/06/2026): the column decides
 * (another row with 30/11 or 11/30), then this browser's locale, and the import dialog lets
 * the user switch.
 */

/** Day before month (30/11/2026) or month before day (11/30/2026). */
export type DateOrder = 'DMY' | 'MDY';

const MONTHS: Record<string, number> = { sept: 9 };
['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december']
  .forEach((name, i) => { MONTHS[name] = i + 1; MONTHS[name.slice(0, 3)] = i + 1; });
// Arabic month names: Gulf / Egyptian, then Levantine (two-word names are joined with _ first).
([
  ['يناير', 1], ['فبراير', 2], ['مارس', 3], ['أبريل', 4], ['ابريل', 4], ['إبريل', 4], ['مايو', 5], ['يونيو', 6], ['يونية', 6],
  ['يوليو', 7], ['يولية', 7], ['أغسطس', 8], ['اغسطس', 8], ['سبتمبر', 9], ['أكتوبر', 10], ['اكتوبر', 10], ['نوفمبر', 11], ['ديسمبر', 12],
  ['كانون_الثاني', 1], ['شباط', 2], ['آذار', 3], ['اذار', 3], ['نيسان', 4], ['أيار', 5], ['ايار', 5], ['حزيران', 6], ['تموز', 7],
  ['آب', 8], ['اب', 8], ['أيلول', 9], ['ايلول', 9], ['تشرين_الأول', 10], ['تشرين_الاول', 10], ['تشرين_الثاني', 11],
  ['كانون_الأول', 12], ['كانون_الاول', 12],
] as Array<[string, number]>).forEach(([name, month]) => { MONTHS[name] = month; });

const WEEKDAYS = new Set([
  'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday',
  'mon', 'tue', 'tues', 'wed', 'thu', 'thur', 'thurs', 'fri', 'sat', 'sun',
  'الأحد', 'الاحد', 'الاثنين', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الاربعاء', 'الخميس', 'الجمعة', 'السبت',
]);

/** Date text made plain: Latin digits, no direction marks (Arabic Windows formats add them), lower case. */
function plainDateText(value: unknown): string {
  return String(value ?? '')
    .replace(/[٠-٩]/g, (c) => String(c.charCodeAt(0) - 0x0660))
    .replace(/[۰-۹]/g, (c) => String(c.charCodeAt(0) - 0x06F0))
    .replace(/[‎‏؜‪-‮⁦-⁩]/g, '')
    .replace(/؍/g, '/')
    .replace(/،/g, ',')
    .replace(/[  ]/g, ' ')
    .trim()
    .toLowerCase();
}

// A time after the date: 30/11/2026 10:00, 2026-11-30T10:00:00Z, 30/11/2026 10:00:00 م
const TIME_SUFFIX = /(?:t|\s+)\d{1,2}:\d{2}(?::\d{2}(?:\.\d+)?)?\s*(?:am|pm|a\.m\.|p\.m\.|ص|م)?\s*(?:z|[+-]\d{2}:?\d{2})?$/;
// Day and month as the first two numbers: 30/11/2026, 11-30-26, 05.06.2026
const NUMERIC_DATE = /^(\d{1,2})[\s./-]+(\d{1,2})[\s./-]+\d{2,4}\b/;

/** The day/month order this browser's locale writes dates in: month first only when it leads (en-US). */
export function localeDateOrder(locale?: string): DateOrder {
  try {
    const types = new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit', year: 'numeric' })
      .formatToParts(new Date(2026, 10, 30)).map((p) => p.type);
    return types.indexOf('month') < types.indexOf('day') && types.indexOf('month') < types.indexOf('year') ? 'MDY' : 'DMY';
  } catch {
    return 'DMY';
  }
}

/** The order a column's dates show: a first number above 12 means day first, a second one month first. Null when none tells. */
export function inferDateOrder(values: unknown[]): DateOrder | null {
  let dayFirst = 0;
  let monthFirst = 0;
  for (const v of values) {
    const m = typeof v === 'string' ? NUMERIC_DATE.exec(plainDateText(v)) : null;
    if (!m) continue;
    const a = Number(m[1]);
    const b = Number(m[2]);
    if (a > 12 && b <= 12) dayFirst++;
    else if (b > 12 && a <= 12) monthFirst++;
  }
  if (dayFirst && !monthFirst) return 'DMY';
  if (monthFirst && !dayFirst) return 'MDY';
  return null;
}

/** True when some date reads differently day-first and month-first (05/06/2026, not 05/05 or 30/11). */
export function hasAmbiguousDates(values: unknown[]): boolean {
  return values.some((v) => {
    const m = typeof v === 'string' ? NUMERIC_DATE.exec(plainDateText(v)) : null;
    return !!m && Number(m[1]) <= 12 && Number(m[2]) <= 12 && Number(m[1]) !== Number(m[2]);
  });
}

/** A Hijri (Umm al-Qura) date as YYYY-MM-DD, through the browser's Islamic calendar; null when it does not exist. */
function hijriToIso(y: number, m: number, d: number): string | null {
  if (m < 1 || m > 12 || d < 1 || d > 30) return null;
  let fmt: Intl.DateTimeFormat;
  try {
    fmt = new Intl.DateTimeFormat('en-u-ca-islamic-umalqura-nu-latn', { timeZone: 'UTC', year: 'numeric', month: 'numeric', day: 'numeric' });
  } catch {
    return null;
  }
  const matches = (ms: number) => {
    const p: Record<string, number> = {};
    for (const part of fmt.formatToParts(new Date(ms))) if (part.type === 'year' || part.type === 'month' || part.type === 'day') p[part.type] = parseInt(part.value, 10);
    return p.year === y && p.month === m && p.day === d;
  };
  // Hijri years average 354.367 days from July 622: guess, then look at the days around it.
  const guess = Date.UTC(622, 6, 16) + Math.round((y - 1) * 354.367 + (m - 1) * 29.53 + d - 1) * 86400000;
  for (let i = 0; i <= 40; i++) {
    for (const ms of i ? [guess + i * 86400000, guess - i * 86400000] : [guess]) {
      if (matches(ms)) {
        const g = new Date(ms);
        return isoDate(g.getUTCFullYear(), g.getUTCMonth() + 1, g.getUTCDate());
      }
    }
  }
  return null;
}

/** YYYY-MM-DD from a year as written (26, 2026, or Hijri 1448 / 48), a month and a day. */
function dateFromParts(year: string, month: number, day: number, now: Date): string | null {
  let y = Number(year);
  if (year.length === 2) {
    y += 2000; // Excel shows 2026 as 26
    // A two-digit year far ahead is a Hijri one: Saudi PCs show 20/06/48 for 20 Jumada II 1448.
    if (y > now.getFullYear() + 15) return hijriToIso(1400 + Number(year), month, day);
  } else if (year.length !== 4) {
    return null;
  }
  if (y >= 1300 && y <= 1600) return hijriToIso(y, month, day);
  if (y < 1900 || y > 2999) return null;
  return isoDate(y, month, day);
}

function serialToIso(serial: number): string | null {
  if (!Number.isFinite(serial) || serial < 1 || serial > 2958465) return null;
  const d = new Date(Math.round((serial - 25569) * 86400 * 1000)); // Excel day 25569 = 1970-01-01
  return isoDate(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate());
}

/**
 * A date cell as YYYY-MM-DD, whatever the PC's date format (see above). `order` settles
 * 05/06/2026; a day above 12 settles itself. '' is null; unreadable is 'invalid'.
 */
export function parseDate(value: unknown, opts: { order?: DateOrder; now?: Date } = {}): string | null | 'invalid' {
  const now = opts.now || new Date();
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? 'invalid' : isoDate(value.getFullYear(), value.getMonth() + 1, value.getDate()) ?? 'invalid';
  }
  if (typeof value === 'number') return serialToIso(value) ?? 'invalid';
  const text = plainDateText(value).replace(TIME_SUFFIX, '');
  if (!text) return null;
  if (/^\d+(\.\d+)?$/.test(text)) {
    if (/^\d{8}$/.test(text)) { // 20261130
      const iso = dateFromParts(text.slice(0, 4), Number(text.slice(4, 6)), Number(text.slice(6)), now);
      if (iso) return iso;
    }
    return serialToIso(Number(text)) ?? 'invalid'; // Excel's date number, e.g. 46356
  }
  const tokens = text
    .replace(/(كانون|تشرين)\s+(الأول|الاول|الثاني)/g, '$1_$2')
    .split(/[\s,./-]+/)
    .map((t) => t.replace(/^(\d+)(?:st|nd|rd|th)$/, '$1'))
    .filter((t) => t && !WEEKDAYS.has(t));
  if (tokens.length !== 3) return 'invalid';
  const isNum = tokens.map((t) => /^\d+$/.test(t));
  const named = tokens.map((t) => MONTHS[t] || 0);
  let iso: string | null = null;
  if (isNum.every(Boolean)) {
    const [a, b, c] = tokens;
    if (a.length === 4) {
      iso = dateFromParts(a, Number(b), Number(c), now); // 2026/11/30
    } else if (a.length <= 2 && b.length <= 2) {
      const x = Number(a);
      const y = Number(b);
      if (x > 12 && y > 12) return 'invalid';
      const dayFirst = x > 12 || (y <= 12 && opts.order !== 'MDY');
      iso = dayFirst ? dateFromParts(c, y, x, now) : dateFromParts(c, x, y, now);
    }
  } else if (isNum[0] && named[1] && isNum[2]) {
    iso = tokens[0].length === 4
      ? dateFromParts(tokens[0], named[1], Number(tokens[2]), now) // 2026-Nov-30
      : dateFromParts(tokens[2], named[1], Number(tokens[0]), now); // 30-Nov-26
  } else if (named[0] && isNum[1] && isNum[2]) {
    iso = dateFromParts(tokens[2], named[0], Number(tokens[1]), now); // Nov 30, 2026
  }
  return iso ?? 'invalid';
}

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** YYYY-MM-DD as 30 Nov 2026: reads the same on every PC. */
export function displayDate(iso: unknown): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso ?? ''));
  return m ? `${Number(m[3])} ${MONTH_ABBR[Number(m[2]) - 1]} ${m[1]}` : String(iso ?? '');
}

export type ImportedLine = Partial<Record<ImportField, string | number>>;

export interface ImportRow {
  /** The row in the sheet or paste, 1-based, header row included. */
  rowNumber: number;
  line: ImportedLine;
  errors: string[];
}

export interface ImportResult {
  rows: ImportRow[];
  /** No row of column names: the columns were taken in the template's order. */
  positional: boolean;
  fields: ImportField[];
  unknownHeaders: string[];
  /** How dates like 05/06/2026 were read. */
  dateOrder: DateOrder;
  /** The first date that reads differently day-first and month-first (05/06/2026) when no other row settled the order; else null. */
  ambiguousDate: string | null;
}

/**
 * Lines from a sheet (an array of rows of cells). The first row of column names decides which
 * column is which; without one, the columns follow the template's order. Empty rows are skipped.
 * Dates follow the column's own day/month order, else `dateOrder`, else this browser's locale.
 */
export function parseImportRows(
  table: unknown[][],
  opts: { requisitionType?: string; serviceUnitPriceMustBeOne?: boolean; dateOrder?: DateOrder; now?: Date } = {},
): ImportResult {
  const requisitionType = opts.requisitionType || '';
  const rows = (table || []).map((r) => (Array.isArray(r) ? r : []));
  const headerIndex = rows.findIndex((r) => r.some((c) => String(c ?? '').trim() !== ''));
  const positional = headerIndex < 0 || !isHeaderRow(rows[headerIndex]);
  const unknownHeaders: string[] = [];
  let fields: Array<ImportField | null>;
  if (positional) {
    fields = templateColumns(requisitionType).map((c) => c.field);
  } else {
    fields = rows[headerIndex].map((c) => {
      const f = fieldForHeader(c);
      if (!f && String(c ?? '').trim()) unknownHeaders.push(String(c).trim());
      return f;
    });
  }
  const columnOf = new Map<ImportField, ImportColumn>(PR_IMPORT_COLUMNS.map((c) => [c.field, c]));
  const service = requisitionType === 'Service';
  const out: ImportRow[] = [];
  const start = positional ? Math.max(0, headerIndex) : headerIndex + 1;
  const dateColumn = fields.indexOf('deliverydate');
  const dates = dateColumn < 0 ? [] : rows.slice(start).map((r) => r[dateColumn]);
  const columnOrder = inferDateOrder(dates);
  const dateOrder = columnOrder || opts.dateOrder || localeDateOrder();
  const ambiguous = columnOrder ? undefined : dates.find((d) => hasAmbiguousDates([d]));
  for (let r = start; r < rows.length; r++) {
    const cells = rows[r];
    if (!cells.some((c) => String(c ?? '').trim() !== '')) continue;
    const line: ImportedLine = {};
    const errors: string[] = [];
    fields.forEach((field, i) => {
      if (!field) return;
      const raw = cells[i];
      const col = columnOf.get(field)!;
      if (col.kind === 'number') {
        const n = parseNumber(raw);
        if (n === null) return;
        if (Number.isNaN(n) || n < 0) errors.push(`${col.header} "${String(raw).trim()}" is not a number`);
        else line[field] = n;
      } else if (col.kind === 'date') {
        const d = parseDate(raw, { order: dateOrder, now: opts.now });
        if (d === null) return;
        if (d === 'invalid') errors.push(`${col.header} "${String(raw).trim()}" is not a date`);
        else line[field] = d;
      } else {
        const s = String(raw ?? '').trim();
        if (s) line[field] = s;
      }
    });
    if (!line.itemcode && !line.itemdescription) errors.push('Item Code or Item Description is required');
    if (line.quantity === undefined) {
      if (!errors.some((e) => e.startsWith('Req. Qty'))) errors.push('Req. Qty is required');
    } else if (!(Number(line.quantity) > 0)) errors.push('Req. Qty must be more than 0');
    // Item codes starting with 62 are service items on any PR type.
    if ((service || String(line.itemcode || '').startsWith('62')) && opts.serviceUnitPriceMustBeOne) {
      if (line.unitprice === undefined) line.unitprice = 1;
      else if (Number(line.unitprice) !== 1) errors.push('Unit Price must be 1 on service lines in this company');
    }
    out.push({ rowNumber: r + 1, line, errors });
  }
  return {
    rows: out,
    positional,
    fields: fields.filter((f): f is ImportField => !!f),
    unknownHeaders,
    dateOrder,
    ambiguousDate: ambiguous === undefined ? null : String(ambiguous).trim(),
  };
}
